// src/utils/axios.ts
import axios from "axios";
import { jwtDecode } from "jwt-decode";
var apiBaseUrl = "";
var axiosInstance = null;
var log = (...args) => console.info("[identity-core][axios]", ...args);
var INVITE_CODE_STORAGE_KEY = "coremind:pending-invite-code";
var workspaceStorageKey = () => {
  const siteHostname = typeof window !== "undefined" ? window.__COREMIND_SITE_HOSTNAME__ : null;
  return siteHostname ? `coremind:workspace-id:${siteHostname}` : "coremind:workspace-id";
};
var getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};
var isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1e3 < Date.now();
  } catch (error) {
    console.error("\u274C Error decoding token:", error);
    return true;
  }
};
var setApiBaseUrl = (url) => {
  apiBaseUrl = url;
  log("setApiBaseUrl", apiBaseUrl);
  axiosInstance = createAxiosInstance();
};
var createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: apiBaseUrl,
    headers: {
      "Content-Type": "application/json"
    },
    withCredentials: true
    // ⚠️ Keep this if your backend expects cookies on the request
  });
  instance.interceptors.request.use((config) => {
    const base = config.baseURL ?? apiBaseUrl;
    if (typeof base === "string" && /\/api\/v1\/?$/.test(base) && typeof config.url === "string" && config.url.startsWith("/api/v1/")) {
      config.url = config.url.replace(/^\/api\/v1/, "");
    }
    const token = getCookie("accessToken") || getCookie("idToken");
    if (token && !isTokenExpired(token)) {
      config.headers["Authorization"] = `Bearer ${token}`;
      console.log("\u2705 Token attached to request.");
    } else {
      console.warn("\u26A0\uFE0F No valid access token found. Request may fail.");
    }
    if (typeof window !== "undefined") {
      const workspaceId = window.localStorage.getItem(workspaceStorageKey());
      if (workspaceId) {
        config.headers["X-Tenant-Id"] = workspaceId;
      }
      const inviteCode = window.sessionStorage.getItem(INVITE_CODE_STORAGE_KEY);
      if (inviteCode) {
        config.headers["X-Invite-Code"] = inviteCode;
      }
      const siteHostname = window.__COREMIND_SITE_HOSTNAME__;
      if (typeof siteHostname === "string" && siteHostname.trim()) {
        config.headers["X-Coremind-Site-Host"] = siteHostname.trim();
      }
    }
    return config;
  });
  return instance;
};
var getAxios = () => {
  if (!axiosInstance) {
    if (typeof window !== "undefined") {
      const windowApi = window.__COREMIND_API_URL__;
      if (typeof windowApi === "string" && windowApi.trim()) {
        setApiBaseUrl(windowApi.trim());
      }
    }
  }
  if (!axiosInstance) {
    const message = "Axios instance not initialized. Call setApiBaseUrl() first.";
    log(message, {
      apiBaseUrl,
      hasWindowApi: typeof window !== "undefined" && window.__COREMIND_API_URL__
    });
    throw new Error(message);
  }
  return axiosInstance;
};

// src/configureAmplify.ts
import { Amplify } from "aws-amplify";
import "aws-amplify/auth/enable-oauth-listener";
function configureAmplify(cfg) {
  const {
    userPoolId,
    userPoolClientId,
    identityPoolId,
    allowGuestAccess = false,
    oauth
  } = cfg;
  if (!userPoolId || !userPoolClientId) {
    throw new Error("[identity-core] configureAmplify: missing userPoolId/userPoolClientId");
  }
  const normalizeRedirects = (value) => Array.isArray(value) ? value : String(value).split(",").map((entry) => entry.trim()).filter(Boolean);
  const base = {
    userPoolId,
    userPoolClientId,
    loginWith: oauth ? {
      email: true,
      oauth: {
        domain: oauth.domain,
        scopes: oauth.scopes,
        redirectSignIn: normalizeRedirects(oauth.redirectSignIn),
        redirectSignOut: normalizeRedirects(oauth.redirectSignOut),
        responseType: oauth.responseType ?? "code",
        providers: oauth.providers
      }
    } : { email: true },
    signUpVerificationMethod: "code",
    userAttributes: { email: { required: true } },
    passwordFormat: {
      minLength: 8,
      requireLowercase: true,
      requireUppercase: true,
      requireNumbers: true,
      requireSpecialCharacters: true
    }
  };
  const cognito = identityPoolId ? { ...base, identityPoolId, allowGuestAccess } : base;
  Amplify.configure({
    Auth: {
      Cognito: cognito
      // TS: CognitoUserPoolConfig | (… & CognitoIdentityPoolConfig)
    }
  });
}

// src/context/AuthContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import {
  fetchAuthSession,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  signInWithRedirect
} from "aws-amplify/auth";
import { jwtDecode as jwtDecode2 } from "jwt-decode";

// src/utils/cookies.ts
var setCookie = (name, value, days = 1) => {
  const date = /* @__PURE__ */ new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1e3);
  const hostname = window.location.hostname;
  const domain = getCookieDomain(hostname);
  const domainAttribute = domain ? `; domain=${domain}` : "";
  const encodedValue = encodeURIComponent(value);
  document.cookie = `${name}=${encodedValue}; expires=${date.toUTCString()}; path=/${domainAttribute}; SameSite=None; Secure`;
};
var getCookie2 = (name) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};
var deleteCookie = (name) => {
  expireCookie(name);
};
var getCookieDomain = (hostname) => {
  if (hostname === "localhost") {
    return null;
  }
  if (hostname === "offsetmind.com" || hostname.endsWith(".offsetmind.com")) {
    return ".offsetmind.com";
  }
  if (hostname.endsWith(".coremind.com")) {
    return ".coremind.com";
  }
  if (hostname === "dev.coremind") {
    return "dev.coremind";
  }
  return null;
};
var expireCookie = (name) => {
  const hostname = window.location.hostname;
  const domains = /* @__PURE__ */ new Set([null, hostname, getCookieDomain(hostname)]);
  const expires = "Thu, 01 Jan 1970 00:00:00 UTC";
  for (const domain of domains) {
    const domainAttribute = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=; Max-Age=0; expires=${expires}; path=/${domainAttribute}; SameSite=None; Secure`;
    document.cookie = `${name}=; Max-Age=0; expires=${expires}; path=/${domainAttribute}`;
  }
};

// src/profileStore.ts
var _store = null;
function setProfileStore(next) {
  _store = next;
}
function getProfileStore() {
  return _store;
}

// src/utils/env.ts
function getViteEnv() {
  try {
    return new Function("try { return import.meta.env } catch { return undefined }")();
  } catch {
    return void 0;
  }
}
function env(key, fallback = null) {
  const vite = getViteEnv();
  if (vite && key in vite) return vite[key];
  if (typeof process !== "undefined" && process.env && key in process.env) {
    return process.env[key];
  }
  const injected = globalThis?.__ENV__;
  if (injected && key in injected) return injected[key];
  return fallback;
}

// src/context/AuthContext.tsx
import { jsx } from "react/jsx-runtime";
var getAuthContext = () => {
  if (typeof window !== "undefined") {
    const existing = window.__COREMIND_AUTH_CONTEXT__;
    if (existing) {
      return existing;
    }
    const created = createContext(void 0);
    window.__COREMIND_AUTH_CONTEXT__ = created;
    return created;
  }
  return createContext(void 0);
};
var AuthContext = getAuthContext();
var validateToken = (token) => {
  if (!token) return null;
  try {
    const decoded = jwtDecode2(token);
    return decoded.exp * 1e3 > Date.now() ? { ...decoded, email_verified: decoded.email_verified ?? false } : null;
  } catch {
    return null;
  }
};
var resolveTenantId = (decoded) => {
  const fromToken = decoded?.["custom:tenant_id"] || decoded?.["tenant_id"] || null;
  const fromEnv = env("VITE_TENANT_ID", null);
  const fromWindow = window.__TENANT_ID__ || null;
  return String(fromToken || fromEnv || fromWindow || "offsetmind");
};
var userFromToken = (t) => {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const u = {
    uuid: t.sub,
    email: t.email,
    name: t.email?.split("@")[0] ?? "user",
    created_at: now,
    first_seen: now,
    last_seen: now,
    updated_at: now,
    is_deleted: false
  };
  const tenantId = resolveTenantId(t);
  if (tenantId) u.tenant_id = tenantId;
  return u;
};
var METADATA_CACHE_MS = 5 * 60 * 1e3;
var metadataCache = null;
var CONTACT_INVITE_STORAGE_KEY = "coremind:contact-invite";
var CONTACT_PROFILE_STORAGE_KEY = "coremind:contact-invite-profile";
var INVITE_CODE_STORAGE_KEY2 = "coremind:pending-invite-code";
var isMetadataCached = (sub) => Boolean(metadataCache && metadataCache.sub === sub && metadataCache.expiresAt > Date.now());
var cacheMetadata = (sub, user) => {
  metadataCache = {
    sub,
    user,
    expiresAt: Date.now() + METADATA_CACHE_MS
  };
};
var ensureUserMetadata = async (t) => {
  if (isMetadataCached(t.sub)) {
    return metadataCache.user;
  }
  const store = getProfileStore();
  const now = (/* @__PURE__ */ new Date()).toISOString();
  if (!store) {
    const fallback = userFromToken(t);
    cacheMetadata(t.sub, fallback);
    return fallback;
  }
  const existing = await store.getUser(t.sub);
  if (!existing) {
    const created = userFromToken(t);
    await store.putUser(t.sub, created);
    cacheMetadata(t.sub, created);
    return created;
  }
  const tenantId = resolveTenantId(t);
  const existingLastSeen = Number.isNaN(Date.parse(existing.last_seen ?? "")) ? 0 : Date.parse(existing.last_seen ?? "");
  const shouldUpdateLastSeen = Date.now() - existingLastSeen > METADATA_CACHE_MS;
  const emailNeedsFilling = !existing.email && t.email;
  const tenantNeedsFilling = Boolean(tenantId && !existing.tenant_id);
  const merged = {
    ...existing,
    email: emailNeedsFilling ? t.email : existing.email,
    last_seen: shouldUpdateLastSeen ? now : existing.last_seen,
    updated_at: shouldUpdateLastSeen ? now : existing.updated_at
  };
  if (tenantNeedsFilling) {
    merged.tenant_id = tenantId;
  }
  const needsWrite = shouldUpdateLastSeen || emailNeedsFilling || tenantNeedsFilling;
  if (needsWrite) {
    await store.putUser(t.sub, merged);
  }
  cacheMetadata(t.sub, merged);
  return merged;
};
var readUserProfileWithRetry = async (uuid, retries = 3, delayMs = 400) => {
  const store = getProfileStore();
  if (!store) throw new Error("No profile store configured");
  for (let i = 0; i < retries; i++) {
    const u = await store.getUser(uuid);
    if (u) return u;
    if (i < retries - 1) await new Promise((r) => setTimeout(r, delayMs));
  }
  throw new Error("User profile not found in profile store");
};
var resolveGroups = (idToken, accessTokenRaw) => {
  const normalize = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map(String);
    return [String(value)];
  };
  const idGroups = normalize(idToken ? idToken["cognito:groups"] : void 0);
  if (idGroups.length) return idGroups;
  if (accessTokenRaw) {
    try {
      const decoded = jwtDecode2(accessTokenRaw);
      const fromAccess = normalize(decoded?.["cognito:groups"]);
      if (fromAccess.length) return fromAccess;
    } catch (err) {
      console.warn("Failed to decode access token for group claims", err);
    }
  }
  return [];
};
var ensureBackendUser = async () => {
  try {
    await getAxios().get("/api/v1/apps/access-requests/mine");
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(INVITE_CODE_STORAGE_KEY2);
    }
  } catch (error) {
    const detail = typeof error === "object" && error && "response" in error ? error.response?.data?.detail : null;
    if (typeof window !== "undefined" && typeof detail === "string" && detail.toLowerCase().includes("invite")) {
      window.dispatchEvent(new CustomEvent("coremind:invite-required"));
    }
    console.warn("Failed to synchronize user with backend:", error);
  }
};
function amplifyCredsToAws(c) {
  if (!c?.accessKeyId || !c?.secretAccessKey) return void 0;
  return {
    accessKeyId: c.accessKeyId,
    secretAccessKey: c.secretAccessKey,
    sessionToken: c.sessionToken,
    expiration: typeof c.expiration === "string" ? Date.parse(c.expiration) : c.expiration instanceof Date ? c.expiration.getTime() : typeof c.expiration === "number" ? c.expiration : void 0
  };
}
var buildSessionFromTokens = async (accessToken, idToken, current) => {
  const decoded = validateToken(idToken);
  if (!decoded) {
    throw new Error("Invalid ID token");
  }
  let user;
  try {
    user = await ensureUserMetadata(decoded);
  } catch {
    user = userFromToken(decoded);
  }
  await ensureBackendUser();
  const groups = resolveGroups(decoded, accessToken);
  user.groups = groups;
  return {
    user,
    token: {
      exp: decoded.exp,
      groups,
      email_verified: decoded.email_verified ?? false
    },
    aws: amplifyCredsToAws(current?.credentials),
    identityId: current ? current?.identityId : void 0
  };
};
var AuthProvider = ({
  children
}) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__coremindAuth = { session, loading };
  }, [session, loading]);
  const handleSignOut = useCallback(async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.warn("Amplify signOut warning:", error);
    } finally {
      deleteCookie("accessToken");
      deleteCookie("idToken");
      setSession(null);
      metadataCache = null;
    }
  }, []);
  const signUp = useCallback(
    async (args) => {
      const {
        email,
        password,
        firstName = "",
        lastName = "",
        phone,
        tenantId = resolveTenantId(null) || "offsetmind",
        inviteCode
      } = args;
      await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
            ...phone ? { phone_number: phone } : {},
            // ⬇️ important for ABAC session-tag mapping
            "custom:tenant_id": tenantId
          },
          // If you use pre/post sign-up Lambda triggers, you can pass metadata:
          clientMetadata: inviteCode ? { inviteCode } : void 0
        }
      });
      return { email };
    },
    []
  );
  useEffect(() => {
    (async () => {
      try {
        let current = await fetchAuthSession();
        let accessToken = current?.tokens?.accessToken?.toString() || null;
        let idToken = current?.tokens?.idToken?.toString() || null;
        const decoded = validateToken(idToken);
        if (!accessToken || !idToken || !decoded) {
          current = await fetchAuthSession({ forceRefresh: true });
          accessToken = current?.tokens?.accessToken?.toString() || null;
          idToken = current?.tokens?.idToken?.toString() || null;
        }
        if (!accessToken || !idToken) {
          accessToken = getCookie2("accessToken");
          idToken = getCookie2("idToken");
          if (!accessToken || !idToken || !validateToken(idToken)) {
            throw new Error("No valid tokens found");
          }
          setSession(await buildSessionFromTokens(accessToken, idToken));
          return;
        }
        setCookie("accessToken", accessToken);
        setCookie("idToken", idToken);
        setSession(await buildSessionFromTokens(accessToken, idToken, current));
      } catch (err) {
        const accessToken = getCookie2("accessToken");
        const idToken = getCookie2("idToken");
        if (accessToken && idToken && validateToken(idToken)) {
          try {
            setSession(await buildSessionFromTokens(accessToken, idToken));
            return;
          } catch (cookieErr) {
            console.warn("Cookie session restore failed:", cookieErr);
          }
        }
        const expectedMissingSession = err instanceof Error && err.message === "No valid tokens found";
        if (!expectedMissingSession) {
          console.error("Initial session restore failed:", err);
        }
        await handleSignOut();
      } finally {
        setLoading(false);
      }
    })();
  }, [handleSignOut]);
  useEffect(() => {
    if (!session?.token?.exp) return;
    const msLeft = session.token.exp * 1e3 - Date.now();
    const refreshIn = Math.max(msLeft - 5 * 60 * 1e3, 5e3);
    const tmr = setTimeout(async () => {
      try {
        const next = await fetchAuthSession({ forceRefresh: true });
        const newAccessToken = next?.tokens?.accessToken?.toString();
        const newIdToken = next?.tokens?.idToken?.toString();
        if (!newAccessToken || !newIdToken) {
          const cookieAccessToken = getCookie2("accessToken");
          const cookieIdToken = getCookie2("idToken");
          if (cookieAccessToken && cookieIdToken && validateToken(cookieIdToken)) {
            setSession(await buildSessionFromTokens(cookieAccessToken, cookieIdToken));
            return;
          }
          await handleSignOut();
          return;
        }
        setCookie("accessToken", newAccessToken);
        setCookie("idToken", newIdToken);
        const redecoded = validateToken(newIdToken);
        if (!redecoded) {
          await handleSignOut();
          return;
        }
        let user = session?.user ?? userFromToken(redecoded);
        try {
          user = await ensureUserMetadata(redecoded);
        } catch (e) {
          console.warn("Background metadata sync failed:", e);
        }
        await ensureBackendUser();
        const aws = amplifyCredsToAws(next?.credentials);
        const identityId = next?.identityId;
        const groups = resolveGroups(redecoded, newAccessToken);
        user.groups = groups;
        setSession({
          user,
          token: {
            exp: redecoded.exp,
            groups,
            email_verified: redecoded.email_verified ?? false
          },
          aws,
          identityId
        });
      } catch (err) {
        const cookieAccessToken = getCookie2("accessToken");
        const cookieIdToken = getCookie2("idToken");
        if (cookieAccessToken && cookieIdToken && validateToken(cookieIdToken)) {
          try {
            setSession(await buildSessionFromTokens(cookieAccessToken, cookieIdToken));
            return;
          } catch (cookieErr) {
            console.warn("Cookie background session restore failed:", cookieErr);
          }
        }
        console.error("Background refresh error:", err);
        await handleSignOut();
      }
    }, refreshIn);
    return () => clearTimeout(tmr);
  }, [handleSignOut, session?.token?.exp, session?.user]);
  const refreshUserFromDB = useCallback(async () => {
    try {
      if (!session?.user?.uuid) return null;
      const store = getProfileStore();
      if (!store) return session.user;
      const fresh = await readUserProfileWithRetry(session.user.uuid);
      setSession((prev) => prev ? { ...prev, user: fresh } : prev);
      return fresh;
    } catch (err) {
      console.error("Failed to refresh user profile:", err);
      return null;
    }
  }, [session?.user?.uuid]);
  const signIn = useCallback(async (email, password) => {
    const normalizeSignInErrorMessage = (error) => {
      const rawMessage = String(error?.message || "");
      const rawCode = String(error?.code || error?.name || "").toLowerCase();
      const normalizedMessage = rawMessage.toLowerCase();
      if (rawCode.includes("notauthorized") || normalizedMessage.includes("incorrect username or password") || normalizedMessage.includes("incorrect password") || normalizedMessage.includes("incorrect username") || normalizedMessage.includes("not authorized")) {
        return "Incorrect email or password. Please try again.";
      }
      if (normalizedMessage.includes("user does not exist")) {
        return "No account was found for that email address.";
      }
      return rawMessage || "Sign-in failed. Please try again.";
    };
    const performSignIn = async () => {
      await amplifySignIn({ username: email, password });
      const current = await fetchAuthSession({ forceRefresh: true });
      const accessToken = current?.tokens?.accessToken?.toString();
      const idToken = current?.tokens?.idToken?.toString();
      if (!accessToken || !idToken)
        throw new Error("Failed to retrieve tokens after sign-in");
      setCookie("accessToken", accessToken);
      setCookie("idToken", idToken);
      const decoded = validateToken(idToken);
      if (!decoded) throw new Error("Invalid ID token after sign-in");
      let user;
      try {
        user = await ensureUserMetadata(decoded);
      } catch (s3err) {
        console.warn("Metadata sync failed after login (continuing):", s3err);
        user = userFromToken(decoded);
      }
      await ensureBackendUser();
      const aws = amplifyCredsToAws(current?.credentials);
      const identityId = current?.identityId;
      const groups = resolveGroups(decoded, accessToken);
      user.groups = groups;
      setSession({
        user,
        token: {
          exp: decoded.exp,
          groups,
          email_verified: decoded.email_verified ?? false
        },
        aws,
        identityId
      });
    };
    try {
      await performSignIn();
    } catch (err) {
      const message = String(err?.message || "");
      if (message.toLowerCase().includes("already a signed in user")) {
        await handleSignOut();
        await performSignIn();
        return;
      }
      console.group("Sign-in error");
      console.error(err);
      console.log("name:", err?.name, "code:", err?.code, "message:", err?.message);
      console.groupEnd();
      throw new Error(normalizeSignInErrorMessage(err));
    }
  }, [handleSignOut]);
  const signInWithProvider = useCallback(async (provider) => {
    await signInWithRedirect({ provider });
  }, []);
  const redirectToSignin = useCallback(() => {
    const currentUrl = encodeURIComponent(window.location.href);
    window.location.href = `./signin?redirect_uri=${currentUrl}`;
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const tenantId = session?.user?.tenant_id;
    if (tenantId) {
      window.__TENANT_ID__ = tenantId;
    } else {
      delete window.__TENANT_ID__;
    }
  }, [session?.user?.tenant_id]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!session?.user?.uuid) {
      delete window.__USER_ID__;
      delete window.__USER_EMAIL__;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAxios().get("/auth/me");
        if (cancelled) return;
        if (data?.user_id) window.__USER_ID__ = data.user_id;
        if (data?.tenant_id) window.__TENANT_ID__ = data.tenant_id;
        if (data?.email) window.__USER_EMAIL__ = data.email;
      } catch (err) {
        console.warn("[identity-core] failed to resolve /auth/me", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.uuid]);
  useEffect(() => {
    if (!session?.user?.uuid || typeof window === "undefined") {
      return;
    }
    const raw = window.sessionStorage.getItem(CONTACT_INVITE_STORAGE_KEY);
    if (!raw) {
      return;
    }
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
    if (!parsed?.request_token) {
      window.sessionStorage.removeItem(CONTACT_INVITE_STORAGE_KEY);
      return;
    }
    window.sessionStorage.removeItem(CONTACT_INVITE_STORAGE_KEY);
    getAxios().post("/contacts/invitations/claim", { request_token: parsed.request_token }).catch((error) => {
      console.warn("Failed to claim contact invitation:", error);
    });
  }, [session?.user?.uuid]);
  useEffect(() => {
    if (!session?.user?.uuid || typeof window === "undefined") {
      return;
    }
    const raw = window.sessionStorage.getItem(CONTACT_PROFILE_STORAGE_KEY);
    if (!raw) {
      return;
    }
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
    window.sessionStorage.removeItem(CONTACT_PROFILE_STORAGE_KEY);
    if (!parsed) {
      return;
    }
    const store = getProfileStore();
    if (!store) {
      return;
    }
    store.getProfile(session.user.uuid).then((profile) => {
      const next = { ...profile ?? {} };
      if (parsed?.first_name && !next.first_name) next.first_name = parsed.first_name;
      if (parsed?.last_name && !next.last_name) next.last_name = parsed.last_name;
      if (parsed?.phone && !next.phone) next.phone = parsed.phone;
      return store.putProfile(session.user.uuid, next);
    }).catch((error) => {
      console.warn("Failed to update profile from contact invite:", error);
    });
  }, [session?.user?.uuid]);
  return /* @__PURE__ */ jsx(
    AuthContext.Provider,
    {
      value: {
        session,
        loading,
        signOut: handleSignOut,
        setSession,
        signIn,
        signInWithProvider,
        signUp,
        // ⬅️ expose
        redirectToSignin,
        refreshUserFromDB
      },
      children
    }
  );
};
var useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

// src/utils/mergeWithRequiredFields.ts
var getRequiredKeys = (schema) => {
  const required = /* @__PURE__ */ new Set();
  const shape = schema.shape;
  for (const key in shape) {
    const field = shape[key];
    const isOptional = field.isOptional?.() || field.isNullable?.() || field._def?.typeName === "ZodOptional" || field._def?.typeName === "ZodNullable";
    if (!isOptional) {
      required.add(key);
    }
  }
  return required;
};
function mergeWithRequiredFields(original, updates, requiredKeys) {
  const result = { ...original, ...updates };
  for (const key of requiredKeys) {
    if (result[key] === void 0 || result[key] === null) {
      result[key] = original[key];
    }
  }
  return result;
}

// src/utils/normalizeWithSchemaDefaults.ts
function normalizeWithSchemaDefaults(data, schema) {
  const parsed = schema.partial().parse(data);
  const withDefaults = schema.parse(parsed);
  return withDefaults;
}

// src/hooks/useCreateTenant.ts
import { useState as useState2 } from "react";
var useCreateTenant = () => {
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const createTenant = async (data) => {
    try {
      setLoading(true);
      const response = await getAxios().post("/api/v1/tenants/accounts", data);
      return response.data;
    } catch (err) {
      setError(err.message || "Unknown error");
      console.error("Create tenant failed:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };
  return { createTenant, loading, error };
};

// src/hooks/useGetAccounts.ts
import { useEffect as useEffect2, useState as useState3 } from "react";
var useGetAccounts = () => {
  const { session } = useAuth();
  const user = session?.user;
  const [accounts, setAccounts] = useState3([]);
  const [loading, setLoading] = useState3(true);
  useEffect2(() => {
    if (!user?.uuid) return;
    const fetchAccounts = async () => {
      try {
        const filter = JSON.stringify({ user_id: user.uuid });
        const res = await getAxios().get("/api/v1/tenants/accounts", {
          params: {
            filter,
            offset: 0,
            limit: 100
          }
        });
        setAccounts(res.data.records || res.data);
      } catch (err) {
        console.error("\u274C Error loading accounts", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, [user?.uuid]);
  return { accounts, loading };
};

// src/hooks/useGetAllAccounts.ts
import { useEffect as useEffect3, useState as useState4 } from "react";
var useGetAllAccounts = () => {
  const [accounts, setAccounts] = useState4([]);
  const [loading, setLoading] = useState4(true);
  const [error, setError] = useState4(null);
  useEffect3(() => {
    const fetchAccounts = async () => {
      try {
        const res = await getAxios().get("/api/v1/tenants/accounts", {
          params: {
            offset: 0,
            limit: 100
          }
        });
        setAccounts(res.data.records || res.data);
      } catch (err) {
        console.error("\u274C Error loading all accounts", err);
        setError(err.message || "Failed to load accounts");
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, []);
  return { accounts, loading, error };
};

// src/hooks/useGetUsers.ts
import { useEffect as useEffect4, useState as useState5 } from "react";
var useGetUsers = () => {
  const [users, setUsers] = useState5([]);
  const [loading, setLoading] = useState5(true);
  const [error, setError] = useState5(null);
  useEffect4(() => {
    const fetchUsers = async () => {
      try {
        const axios2 = getAxios();
        const response = await axios2.get("/api/v1/core/users");
        setUsers(response.data || []);
      } catch (err) {
        console.error("\u274C Failed to fetch users", err);
        setError("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);
  return { users, loading, error };
};

// src/hooks/useUserProfileDoc.ts
import { useCallback as useCallback2, useEffect as useEffect5, useState as useState6 } from "react";
var nowIso = () => (/* @__PURE__ */ new Date()).toISOString();
var defaultProfile = () => ({
  picture: null,
  preferred_name: null,
  phone: null,
  mobile: null,
  country: null,
  timezone: null,
  gender: null,
  birthdate: null,
  language_preference: null,
  occupation: null,
  notes: null,
  admin: false,
  contact: false,
  archived: false,
  deceased: false,
  volunteer: false,
  notification_opt_in: true
});
function useUserProfileDoc() {
  const { session } = useAuth();
  const sub = session?.user?.uuid ?? null;
  const [profile, setProfile] = useState6(null);
  const [loading, setLoading] = useState6(Boolean(sub));
  const [error, setError] = useState6(null);
  const load = useCallback2(async () => {
    if (!sub) return;
    setLoading(true);
    setError(null);
    try {
      const store = getProfileStore();
      if (!store) throw new Error("Profile store not initialized");
      const existing = await store.getProfile(sub) ?? defaultProfile();
      setProfile(existing);
    } catch (e) {
      setError(e?.message ?? "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [sub]);
  const save = useCallback2(
    async (patch) => {
      if (!sub) return;
      setError(null);
      try {
        const store = getProfileStore();
        console.log("the profile store", store);
        if (!store) throw new Error("Profile store not initialized");
        const next = { ...profile ?? defaultProfile(), ...patch, updated_at: nowIso() };
        setProfile(next);
        await store.putProfile(sub, next);
        return next;
      } catch (e) {
        setError(e?.message ?? "Failed to save profile");
        throw e;
      }
    },
    [sub, profile]
  );
  useEffect5(() => {
    if (sub) void load();
  }, [sub, load]);
  return { profile, setProfile, load, save, loading, error };
}

// src/api/serviceKeys.ts
var listAppScopes = async (componentKey, includeInactive) => {
  const params = {};
  if (componentKey) params.component_key = componentKey;
  if (includeInactive) params.include_inactive = true;
  const response = await getAxios().get("/apps/scopes", {
    params: Object.keys(params).length > 0 ? params : void 0
  });
  return response.data;
};
var listServiceKeys = async (includeAll) => {
  const response = await getAxios().get("/auth/service-keys", {
    params: includeAll ? { include_all: true } : void 0
  });
  return response.data;
};
var createServiceKey = async (payload) => {
  const response = await getAxios().post("/auth/service-keys", payload);
  return response.data;
};
var revokeServiceKey = async (serviceKeyId) => {
  const response = await getAxios().delete(`/auth/service-keys/${serviceKeyId}`);
  return response.data;
};
var updateServiceKeyScopes = async (serviceKeyId, scopeIds) => {
  const response = await getAxios().patch(`/auth/service-keys/${serviceKeyId}`, {
    scope_ids: scopeIds
  });
  return response.data;
};
var createAppScope = async (payload) => {
  const response = await getAxios().post("/apps/scopes", {
    is_global: false,
    is_active: true,
    ...payload
  });
  return response.data;
};
var updateAppScope = async (scopeId, payload) => {
  const response = await getAxios().patch(`/apps/scopes/${scopeId}`, payload);
  return response.data;
};
var deleteAppScope = async (scopeId) => {
  const response = await getAxios().delete(`/apps/scopes/${scopeId}`);
  return response.data;
};

// src/schema/user.schema.ts
import { z } from "zod";
var userSchema = z.object({
  uuid: z.string().describe("Unique identifier for the user"),
  first_seen: z.string().describe("First time the user was seen"),
  last_seen: z.string().describe("Last time the user was seen"),
  updated_at: z.string().describe("Last updated timestamp"),
  created_at: z.string().describe("Creation timestamp"),
  is_deleted: z.boolean().optional().nullable().describe("Whether the user is deleted"),
  name: z.string().optional().nullable().describe("Full name"),
  firstname: z.string().describe("First name"),
  preferred_name: z.string().optional().nullable().describe("Preferred name or nickname"),
  middle_name: z.string().optional().nullable().describe("Middle name"),
  lastname: z.string().describe("Last name"),
  email: z.string().email("Invalid email").describe("Email address"),
  phone: z.string().optional().nullable().describe("Phone number"),
  mobile: z.string().optional().nullable().describe("Mobile number"),
  username: z.string().optional().nullable().describe("Username for login"),
  last_login: z.string().optional().nullable().describe("Most recent login timestamp"),
  status: z.string().optional().nullable().describe("User account status"),
  country: z.string().optional().nullable().describe("Country of residence"),
  timezone: z.string().optional().nullable().describe("Timezone identifier"),
  picture: z.string().optional().nullable().describe("Profile picture URL"),
  family_relationship: z.string().optional().nullable().describe("Family relationship details"),
  category_id: z.string().optional().nullable().describe("Assigned category ID"),
  admin: z.boolean().optional().nullable().describe("Is user an admin?"),
  contact: z.boolean().optional().nullable().describe("Is user a contact person?"),
  archived: z.boolean().optional().nullable().describe("Has this user been archived?"),
  deceased: z.boolean().optional().nullable().describe("Is the user marked as deceased?"),
  volunteer: z.boolean().optional().nullable().describe("Is user marked as a volunteer?"),
  family_id: z.string().optional().nullable().describe("Family ID reference"),
  birthdate: z.string().optional().nullable().describe("Date of birth (YYYY-MM-DD)"),
  gender: z.string().optional().nullable().describe("Gender identity"),
  language_preference: z.string().optional().nullable().describe("Preferred language"),
  occupation: z.string().optional().nullable().describe("User's occupation"),
  notes: z.string().optional().nullable().describe("Free-form notes about the user"),
  person_id: z.string().optional().nullable().describe("External person ID"),
  notification_opt_in: z.boolean().optional().nullable().describe("Has user opted into notifications?"),
  preferred_contact_method: z.string().optional().nullable().describe("Preferred contact method")
});

// src/apiProfileStore.ts
var USER_ENDPOINT = "/api/v1/core/users";
var createApiProfileStore = () => ({
  async getUser(sub) {
    const axios2 = getAxios();
    try {
      const { data } = await axios2.get(`${USER_ENDPOINT}/${encodeURIComponent(sub)}`);
      return data ?? null;
    } catch (err) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },
  async putUser(sub, user) {
    const axios2 = getAxios();
    try {
      await axios2.patch(`${USER_ENDPOINT}/${encodeURIComponent(sub)}`, user);
    } catch (err) {
      if (err?.response?.status === 404) {
        await axios2.post(USER_ENDPOINT, user);
        return;
      }
      throw err;
    }
  },
  async userExists(sub) {
    const axios2 = getAxios();
    try {
      await axios2.get(`${USER_ENDPOINT}/${encodeURIComponent(sub)}`);
      return true;
    } catch (err) {
      if (err?.response?.status === 404) return false;
      throw err;
    }
  },
  async getProfile(sub) {
    const axios2 = getAxios();
    try {
      const { data } = await axios2.get(`${USER_ENDPOINT}/${encodeURIComponent(sub)}`);
      return data ?? null;
    } catch (err) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },
  async putProfile(sub, profile) {
    const axios2 = getAxios();
    await axios2.patch(`${USER_ENDPOINT}/${encodeURIComponent(sub)}`, profile);
  }
});
export {
  AuthProvider,
  configureAmplify,
  createApiProfileStore,
  createAppScope,
  createServiceKey,
  deleteAppScope,
  deleteCookie,
  getAxios,
  getCookie2 as getCookie,
  getProfileStore,
  getRequiredKeys,
  listAppScopes,
  listServiceKeys,
  mergeWithRequiredFields,
  normalizeWithSchemaDefaults,
  revokeServiceKey,
  setApiBaseUrl,
  setCookie,
  setProfileStore,
  updateAppScope,
  updateServiceKeyScopes,
  useAuth,
  useCreateTenant,
  useGetAccounts,
  useGetAllAccounts,
  useGetUsers,
  useUserProfileDoc,
  userSchema
};
